import { DeleteBtn, PlayPauseBtn } from "../Utility/Utility";
import { useRef, useCallback, useState } from "react";


// ─────────────────────────────────────────────
// VideoBlock — fixed 16:9 aspect ratio
//
// Overlay strategy:
//   • iframe has pointerEvents:none → YouTube never
//     sees hover, so its chrome stays permanently hidden
//   • Transparent overlay div covers the whole frame
//     and eats all pointer events (no stray hovers)
//   • Custom PlayPauseBtn + DeleteBtn float at top-right
//     above the overlay (z-index 10)
//   • postMessage drives play / pause on the iframe
// ─────────────────────────────────────────────
export function VideoBlock({ item, onDelete, width, deletable = true }) {
  const height = Math.round(width * (9 / 16));
  const iframeRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);

  // Send a command to the YouTube iframe via postMessage
  const sendCommand = useCallback((func) => {
    try {
      iframeRef.current?.contentWindow?.postMessage(
        JSON.stringify({ event: "command", func, args: [] }),
        "*"
      );
    } catch (_) {}
    // Drop focus so YouTube's UI chrome doesn't appear
    iframeRef.current?.blur();
  }, []);

  // Play/Pause toggle — driven by our local state
  const handlePlayPause = useCallback((e) => {
    e.stopPropagation();
    if (isPlaying) {
      sendCommand("pauseVideo");
      setIsPlaying(false);
    } else {
      sendCommand("playVideo");
      setIsPlaying(true);
    }
  }, [isPlaying, sendCommand]);

  // Clicking the overlay also toggles play/pause
  // (convenience — tap anywhere on the video)
  const handleOverlayClick = useCallback((e) => {
    e.stopPropagation();
    handlePlayPause(e);
  }, [handlePlayPause]);

  // Build URL with all noise-reducing params
  const src = (() => {
    try {
      const u = new URL(item.res.url);
      u.searchParams.set("rel",            "0"); // no related videos
      u.searchParams.set("enablejsapi",    "1"); // required for postMessage
      u.searchParams.set("modestbranding", "1"); // smaller YT logo
      u.searchParams.set("iv_load_policy", "3"); // hide annotations
      u.searchParams.set("disablekb",      "1"); // no keyboard shortcuts
      return u.toString();
    } catch {
      return item.res.url;
    }
  })();

  return (
    <div
      style={{
        position: "relative",
        width,
        height,
        borderRadius: 10,
        overflow: "hidden",
        background: "#0d0d0d",
        boxShadow: "0 2px 12px rgba(0,0,0,0.18)",
        flexShrink: 0,
      }}
    >
      {/* iframe: pointerEvents off so YouTube never gets hover/focus */}
      <iframe
        ref={iframeRef}
        src={src}
        width={width}
        height={height}
        frameBorder="0"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        title={`video-${item.ind}`}
        style={{ display: "block", border: "none", pointerEvents: "none" }}
        tabIndex={-1}
      />

      {/* Overlay: eats all pointer/hover — click toggles play/pause */}
      <div
        onClick={handleOverlayClick}
        style={{
          position: "absolute",
          inset: 0,
          zIndex: 5,
          cursor: "pointer",
          background: "transparent",
        }}
      />

      {/* Custom controls — above overlay */}
      <PlayPauseBtn isPlaying={isPlaying} onClick={handlePlayPause} />
      {deletable && <DeleteBtn onDelete={onDelete} />}
    </div>
  );
}